import { createSelector } from 'reselect'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { getRegionByCode } from '@luxuryescapes/lib-regions'
import { getCruiseItineraryCountryCodes } from 'checkout/lib/utils/cruises/itinerary'
import { getViewedOrder } from 'selectors/orderSelectors'

export function isOrderInternationalTrip(
  order: App.Order,
  flightDetails: App.State['orders']['flightDetails'],
  cruiseOfferSnapshots?: App.State['cruise']['cruiseOfferSnapshot'],
): boolean {
  const orderRegion = getRegionByCode(order.regionCode)

  if (!orderRegion) return false

  const orderRegionCode = orderRegion.code.toLowerCase()
  const orderRegionName = orderRegion.name.toLowerCase()

  // Check bedbank items
  if (order.bedbankItems.length) {
    const hasInternationalBedbank = order.bedbankItems.some(item => {
      const countryCode = item.offer?.property?.address?.countryCode
      if (!countryCode) return false
      return countryCode.toLowerCase() !== orderRegionCode
    })
    if (hasInternationalBedbank) return true
  }

  // Check tour items
  if (order.tourItems.length) {
    const hasInternationalTour = order.tourItems.some(item => {
      return item.tour.countriesVisited.some(countryName => {
        return countryName.toLowerCase() !== orderRegionName
      })
    })
    if (hasInternationalTour) return true
  }

  // Check hotel, villa and accommodation items
  if (order.items.length) {
    const hasInternationalHotel = order.items.some(item => {
      const countryCode = item.offer.property?.geoData?.countryCode
      if (!countryCode) return false
      return countryCode.toLowerCase() !== orderRegionCode
    })
    if (hasInternationalHotel) return true
  }

  const flightDetailsByOrderItems = getFlightDetailsByOrderItems(order, flightDetails)

  // Check flight items
  if (order.flightItems.length && flightDetailsByOrderItems) {
    const hasInternationalFlight = order.flightItems.some((item: App.OrderFlightItem) => {
      const details = flightDetailsByOrderItems[item.id]
      if (!details) return false
      return details.bookingJourney.legs.some(leg => {
        const countryCode = leg.arrivalCountryCode
        if (!countryCode) return false
        return countryCode.toLowerCase() !== orderRegionCode
      })
    })
    if (hasInternationalFlight) return true
  }

  // Check cruise items via offer snapshot itinerary
  if (order.cruiseItems.length && cruiseOfferSnapshots) {
    const hasInternationalCruise = order.cruiseItems.some(item => {
      if (item.status === 'cancelled') return false
      const snapshot = cruiseOfferSnapshots[item.bookingId]
      if (!snapshot) return false
      const countryCodes = getCruiseItineraryCountryCodes(snapshot)
      return countryCodes.some(cc => cc.toLowerCase() !== orderRegionCode)
    })
    if (hasInternationalCruise) return true
  }

  return false
}

const getOrderIsLoyaltyInternationalTrip = createSelector(
  (state: App.State) => getViewedOrder(state),
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (order, flightDetails, cruiseOfferSnapshots): boolean =>
    order ? isOrderInternationalTrip(order, flightDetails, cruiseOfferSnapshots) : false,
)

export default getOrderIsLoyaltyInternationalTrip
